import React, { useState, useEffect, useRef } from "react"
import { X, Save, Minimize2, Maximize2, CheckCircle } from "lucide-react"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../services/firebase"
import interviewTrackingService from "../../services/interviewTrackingService"

const InterviewNotes = ({ isOpen, onClose, meetingId, userId, userRole }) => {
  const [notes, setNotes] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const notesRef = useRef(null)
  const saveTimeoutRef = useRef(null)

  // ── Fetch existing notes ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchNotes = async () => {
      if (!meetingId || !userId) return
      try {
        const userInterviewRef = doc(
          db,
          "users",
          userId,
          "interviews",
          meetingId,
        )
        const userInterviewDoc = await getDoc(userInterviewRef)
        if (userInterviewDoc.exists() && userInterviewDoc.data().notes) {
          setNotes(userInterviewDoc.data().notes)
          if (userInterviewDoc.data().notesUpdatedAt)
            setLastSaved(userInterviewDoc.data().notesUpdatedAt.toDate())
        } else {
          const noteDoc = await getDoc(
            doc(db, "rooms", meetingId, "notes", userId),
          )
          if (noteDoc.exists()) {
            setNotes(noteDoc.data().content || "")
            if (noteDoc.data().updatedAt)
              setLastSaved(noteDoc.data().updatedAt.toDate())
          }
        }
      } catch (err) {
        console.error("Error fetching notes:", err)
      }
    }

    if (isOpen) {
      fetchNotes()
      setTimeout(() => notesRef.current?.focus(), 100)
    }
  }, [isOpen, meetingId, userId])

  // ── Auto-save ────────────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(saveTimeoutRef.current)
    if (notes.trim() && isOpen && meetingId && userId) {
      saveTimeoutRef.current = setTimeout(() => saveNotes(), 3000)
    }
    return () => clearTimeout(saveTimeoutRef.current)
  }, [notes, isOpen])

  const saveNotes = async () => {
    if (!meetingId || !userId || !notes.trim()) return
    setIsSaving(true)
    try {
      const success = await interviewTrackingService.saveInterviewNotes(
        meetingId,
        userId,
        notes,
        userRole,
      )
      if (success) {
        setLastSaved(new Date())
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 2000)
      }
    } catch (err) {
      console.error("Error saving notes:", err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = () => {
    clearTimeout(saveTimeoutRef.current)
    saveNotes()
  }

  const formatLastSaved = () =>
    lastSaved?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }) || ""

  if (!isOpen || userRole !== "interviewer") return null

  return (
    <div
      className={`fixed bottom-20 right-8 z-30 bg-white border border-zinc-300 flex flex-col overflow-hidden transition-all duration-200 ${
        isMinimized ? "w-64 h-10" : "w-88 h-[440px]"
      }`}
      style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        boxShadow: "4px 4px 0 #1c1c1c",
        width: isMinimized ? "16rem" : "22rem",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Serif:ital,wght@0,400;1,400&display=swap');
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-zinc-300 bg-white">
        <span
          className="text-xs font-bold uppercase tracking-widest text-zinc-400"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          Notes
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized((p) => !p)}
            className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
            style={{ borderRadius: 0 }}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
            style={{ borderRadius: 0 }}
            title="Close"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* ── Textarea ──────────────────────────────────────────────── */}
          <textarea
            ref={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex-1 w-full px-4 py-4 text-sm text-zinc-800 resize-none focus:outline-none bg-white placeholder:text-zinc-500"
            placeholder="Take notes during the interview..."
            style={{
              fontFamily: "'IBM Plex Serif', Georgia, serif",
              fontSize: "15px",
              lineHeight: "1.8",
              border: "none",
            }}
          />

          {/* ── Footer ────────────────────────────────────────────────── */}
          <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-t border-zinc-200 bg-white">
            {/* Status */}
            <span
              className="text-xs"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                color: saveSuccess ? "#16a34a" : "#a1a1aa",
              }}
            >
              {saveSuccess ? (
                <span className="flex items-center gap-1">
                  <CheckCircle size={11} />
                  saved
                </span>
              ) : lastSaved ? (
                `saved ${formatLastSaved()}`
              ) : (
                "not saved yet"
              )}
            </span>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={isSaving || !notes.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderRadius: 0 }}
            >
              {isSaving ? (
                <>
                  <div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                  saving
                </>
              ) : (
                <>
                  <Save size={11} />
                  Save
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default InterviewNotes
